import { useState, type ReactNode } from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import { Input } from "@/components/ui/Input"
import { Avatar } from "@/components/ui/Avatar"
import type { Person } from "@/lib/person"

const SECTION = "px-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground"
const ROW = "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-foreground"
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

// Trello's Members popover: the selected people first, each with an X to
// remove, then everyone else in the org, click to add. One search box
// filters both sections. Each add/remove hands back the whole resulting
// list — the caller decides whether that saves right away (task drawer) or
// waits for a form's Save (settings cards).
export default function PeoplePickerPopover({ title, people, selectedIds, onChange, selectedHeading = "Selected", children }: {
  title: string
  people: Person[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  // Heading over the selected section: "On this task" on a task, "Selected"
  // elsewhere.
  selectedHeading?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const needle = query.trim().toLowerCase()
  const matches = (p: Person) => !needle || p.name.toLowerCase().includes(needle)
  const selected = people.filter((p) => selectedIds.includes(p.id) && matches(p))
  const others = people.filter((p) => !selectedIds.includes(p.id) && matches(p))

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery("") }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-3">
        <div className="relative flex items-center justify-center">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className={cn("absolute right-0 rounded-sm p-1 text-muted-foreground hover:text-foreground", FOCUS)}
          >
            <X size={15} />
          </button>
        </div>

        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people"
          aria-label="Search people"
        />

        {selected.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className={SECTION}>{selectedHeading}</p>
            <ul className="flex flex-col gap-0.5">
              {selected.map((p) => (
                <li key={p.id} className={cn(ROW, "bg-muted/60")}>
                  <Avatar size="md" name={p.name} initials={p.initials} avatarUrl={p.avatar_url} className="border border-border" />
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${p.name}`}
                    onClick={() => onChange(selectedIds.filter((id) => id !== p.id))}
                    className={cn("rounded-sm p-1 text-muted-foreground hover:text-foreground", FOCUS)}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <p className={SECTION}>Everyone else</p>
          {others.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">No one matches.</p>
          ) : (
            <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
              {others.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onChange([...selectedIds, p.id])}
                    className={cn(ROW, "hover:bg-muted/50", FOCUS)}
                  >
                    <Avatar size="md" name={p.name} initials={p.initials} avatarUrl={p.avatar_url} className="border border-border" />
                    <span className="min-w-0 truncate">{p.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
